import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { MdEdit } from "react-icons/md";
import { FaChevronDown } from "react-icons/fa";
import {
	getAllBrandsByCategory,
	getCategoryDetail,
	getDeliveryTimes,
	getPaymentTerms,
	getSpecifications,
	postEnquiry
} from '../../../API/Api';

const ColdRolledCoils = () => {
	const { slug } = useParams();
	const [product, setProduct] = useState(null);
	const [brands, setBrands] = useState([]);
	const [selectedBrands, setSelectedBrands] = useState([]);
	const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
	const [dimensions, setDimensions] = useState('');
	const [thickness, setThickness] = useState('');
	const [quantity, setQuantity] = useState('');
	const [quantityUnit, setQuantityUnit] = useState('MT');
	const [quoteItems, setQuoteItems] = useState([]);
	const [categoryData, setCategoryData] = useState(null);
	const [specifications, setSpecifications] = useState([]);
	const [selectedSpecification, setSelectedSpecification] = useState('');
	const [paymentTerms, setPaymentTerms] = useState([]);
	const [deliveryTimes, setDeliveryTimes] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [editingRow, setEditingRow] = useState(null);
	const [originalItem, setOriginalItem] = useState(null);
	const [showDeliveryOther, setShowDeliveryOther] = useState(false);
	const [showAdvanceFields, setShowAdvanceFields] = useState(false);

	const [formData, setFormData] = useState({
		address: '',
		pincode: '',
		payment: '',
		delivery: '',
		deliveryOther: '',
		priceBasis: '',
		advanceValue: '',
		advanceType: 'percentage',
		restDays: '',
		paymentDays: '',
		category_id: ''
	});

	const renderBrandsInTable = (brandIds) => {
		if (!Array.isArray(brandIds)) return '';
		return brandIds.map(id => {
			const brand = brands.find(b => b.id === id);
			return brand ? brand.name : '';
		}).join(', ');
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isBrandDropdownOpen && !event.target.closest('.dropdown-multiselect')) {
				setIsBrandDropdownOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isBrandDropdownOpen]);

	useEffect(() => {
		const fetchPaymentTerms = async () => {
			try {
				const response = await getPaymentTerms();
				if (response.status) {
					setPaymentTerms(response.data);
				}
			} catch (error) {
				console.error("Error fetching payment terms:", error);
			}
		};

		fetchPaymentTerms();
	}, []);

	useEffect(() => {
		const fetchCategoryDetail = async () => {
			if (!slug) return;

			try {
				const response = await getCategoryDetail(slug);
				if (response?.status) {
					setCategoryData(response);
					setProduct(response.product_details);

					const rawSpec = response.data?.specification || '';
					const parsedSpecs = rawSpec.split(',').map(spec => spec.trim()).filter(Boolean);
					setSpecifications(parsedSpecs);

					setFormData(prev => ({
						...prev,
						product_id: response.product_details?.id?.toString() || '',
						category_id: response.data?.id?.toString() || ''
					}));
				} else {
					toast.error("Failed to load category details");
				}
			} catch (error) {
				toast.error("Error fetching category details");
			}
		};

		fetchCategoryDetail();
	}, [slug]);

	useEffect(() => {
		const fetchBrands = async () => {
			if (!slug) return;

			try {
				const response = await getAllBrandsByCategory(slug);
				if (response?.status && response.data?.length) {
					setBrands(response.data);
				}
			} catch (error) {
				toast.error("Failed to load brands");
			}
		};
		fetchBrands();
	}, [slug]);

	useEffect(() => {
		const fetchDeliveryTimes = async () => {
			try {
				const response = await getDeliveryTimes();
				if (response.status) {
					setDeliveryTimes(response.data);
				}
			} catch (error) {
				console.error("Error fetching delivery times:", error);
			}
		};

		fetchDeliveryTimes();
	}, []);

	const handleBrandChange = (e) => {
		const brandId = e.target.getAttribute('data-id');
		const brandName = e.target.value;
		const isChecked = e.target.checked;

		if (brandName === "") {
			if (isChecked) {
				setSelectedBrands([{ id: 0, name: "" }]);
			} else {
				setSelectedBrands([]);
			}
		} else {
			if (isChecked) {
				setSelectedBrands(prev => [...prev, { id: parseInt(brandId), name: brandName }]);
			} else {
				setSelectedBrands(prev => prev.filter(brand => brand.id !== parseInt(brandId)));
			}
		}
	};

	const handleReset = () => {
		setDimensions('');
		setThickness('');
		setQuantity('');
		setQuantityUnit('MT');
		setSelectedSpecification('');
		setSelectedBrands([]);
	};

	const handleFormChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		if (name === 'payment') {
			setShowAdvanceFields(value === 'Advance');
		}
		if (name === 'delivery') {
			setShowDeliveryOther(value === 'Others');
		}
	};

	const enforceSixDigitLimit = (e) => {
		if (e.target.value.length > 6) {
			setFormData(prev => ({
				...prev,
				pincode: e.target.value.slice(0, 6)
			}));
		}
	};

	const handleEdit = (index) => {
		if (editingRow === index) {
			cancelEdit();
			return;
		}
		setOriginalItem({ ...quoteItems[index] });
		setEditingRow(index);
	};

	const updateItemField = (index, field, value) => {
		setQuoteItems(prev => prev.map((item, i) =>
			i === index ? { ...item, [field]: value } : item
		));
	};

	const cancelEdit = () => {
		if (originalItem && editingRow !== null) {
			setQuoteItems(prev => prev.map((item, i) =>
				i === editingRow ? originalItem : item
			));
		}
		setEditingRow(null);
		setOriginalItem(null);
	};

	const saveEdit = (index) => {
		setEditingRow(null);
		setOriginalItem(null);
		toast.success("Item updated successfully");
	};

	const handleAddItem = () => {
		if (selectedBrands.length === 0 || !dimensions || !thickness ||
			!selectedSpecification || !quantity) {
			toast.error("Please fill out all fields before adding.");
			return;
		}

		const newItem = {
			brands: selectedBrands.map(b => b.name).join(", "),
			brandIds: selectedBrands.map(b => b.id),
			dimensions: dimensions,
			thickness: thickness,
			specification: selectedSpecification,
			quantity: quantity,
			quantityUnit: quantityUnit
		};

		setQuoteItems(prev => [...prev, newItem]);
		handleReset();
	};

	const removeItem = (index, e) => {
		if (e) e.stopPropagation();
		if (window.confirm("Are you sure you want to remove this item?")) {
			setQuoteItems(prev => prev.filter((_, i) => i !== index));
		}
	};

	const submitQuote = async () => {
		if (isSubmitting) return;
		if (quoteItems.length === 0) {
			toast.error("Please add at least one item before submitting");
			return;
		}

		const { address, pincode, payment, delivery, priceBasis } = formData;

		if (!address || !pincode || !payment || !delivery || !priceBasis) {
			toast.error("Please fill all required fields");
			return;
		}

		setIsSubmitting(true);

		try {
			const enquiry_items = quoteItems.map((item) => {
				return {
					product_id: product?.id?.toString() || '',
					brand_id: item.brandIds.join(','),
					size: item.dimensions.toString(),
					specification: item.specification,
					price_basis: priceBasis,
					address: address,
					pincode: pincode,
					universal_qty: item.quantity.toString(),
					quantity_unit: item.quantityUnit,
					wall_thickness: item.thickness,
					delivery_time: formData.delivery === 'Others' ? formData.deliveryOther : formData.delivery,
					message: "Please provide quotation for these items"
				};
			});

			const now = new Date();
			const finalPayload = {
				payment_term: payment,
				category_id: formData.category_id,
				...(payment === 'Advance' && {
					advance_type: formData.advanceType === 'percentage' ? 'Percentage' : 'Fixed Amount',
					advance_payment_value: formData.advanceValue.toString(),
					delivery_time: formData.delivery === 'Others' ? formData.deliveryOther : formData.delivery,
					rest_paid_within_days: formData.restDays?.toString() || ''
				}),
				enquiry_items,
				shu_start_date_time: now.toISOString(),
				shu_end_date_time: new Date(now.setDate(now.getDate() + 7)).toISOString(),
				delivery_time: delivery,
				address: address,
				pincode: pincode,
				...(payment !== 'Advance' && formData.paymentDays && {
					payment_term_number_days: formData.paymentDays.toString()
				})
			};

			const response = await postEnquiry(finalPayload);

			if (response.status) {
				toast.success(`Enquiry submitted successfully!`);
				setQuoteItems([]);
				handleReset();
				setFormData({
					address: '',
					pincode: '',
					payment: '',
					delivery: '',
					deliveryOther: '',
					priceBasis: '',
					advanceValue: '',
					advanceType: 'percentage',
					restDays: '',
					paymentDays: '',
					category_id: formData.category_id
				});
			} else {
				toast.error(response.message || "Enquiry submission failed");
			}
		} catch (error) {
			console.error("Submission error:", error);
			const errorMessage = error.response?.data?.message || error.message || "Submission failed";
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="tmtProductContainerAngle">
			<ToastContainer />
			<div className="tmtMainContainerAngle">
				<div className="tmtLeftPanelAngle">
					<h1 className="tmtMobileTitleAngle">{categoryData?.data?.name}</h1>
					<img
						src={categoryData?.product_details?.image}
						alt={categoryData?.product_details?.name}
						className="tmtProductImageAngle"
					/>
					{product?.description && (
						<div
							className="product-description-pipe"
							dangerouslySetInnerHTML={{ __html: product.description }}
						/>
					)}
				</div>

				<div className="tmtRightPanelAngle">
					<div className="tmtInfoAngle">
						<h1 className="tmtDesktopTitleAngle">{categoryData?.data?.name}</h1>
						{/* {selectedBrands.length > 0 && (
							<p className="selected-optionAngle">
								<strong>Selected Brands:</strong> {selectedBrands.map(b => b.name).join(", ")}
							</p>
						)} */}
					</div>

					<div className="form-row-angle">
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Brand/Make:<span className="required-asterisk">*</span></label>
								<div className="dropdown-multiselect">
									<div
										className="erw-dropdown-toggle"
										onClick={(e) => {
											e.stopPropagation();
											setIsBrandDropdownOpen(!isBrandDropdownOpen);
										}}
									>
										<span className="dropdown-arrow">
											{selectedBrands.length > 0
												? selectedBrands.map(b => b.name).join(", ")
												: "Select Brand / Make"}
										</span>
										<FaChevronDown />
									</div>
									{isBrandDropdownOpen && (
										<div className="erw-dropdown-options" onClick={(e) => e.stopPropagation()}>
											<div className="brand-checkbox-container">
												{brands.map((brand) => (
													<div key={brand.id} className="brand-checkbox-item">
														<input
															type="checkbox"
															id={`brand-${brand.id}`}
															name="brand"
															value={brand.name}
															data-id={brand.id}
															checked={selectedBrands.some(b => b.id === brand.id)}
															onChange={handleBrandChange}
														/>
														<label htmlFor={`brand-${brand.id}`}>
															{brand.name} {brand.grade && `(${brand.grade})`}
														</label>
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						</div>

						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Width (mm):<span className="required-asterisk">*</span></label>
								<input
									type="text"
									className="tmtDimensionInputAngle"
									value={dimensions}
									onChange={(e) => setDimensions(e.target.value)}
									min="0"
									step="0.1"
									placeholder="Enter width"
								/>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-full-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Thickness (mm):<span className="required-asterisk">*</span></label>
								<input
									type="text"
									className="tmtInputAngle"
									value={thickness}
									onChange={(e) => setThickness(e.target.value)}
									min="0"
									step="0.1"
									placeholder="Enter thickness"
								/>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Quantity:<span className="required-asterisk">*</span></label>
								<input
									type="number"
									placeholder="Enter Quantity"
									min="0.01"
									step="0.01"
									value={quantity}
									onChange={(e) => setQuantity(e.target.value)}
									className="tmtInputAngle"
								/>
							</div>
						</div>
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Unit:<span className="required-asterisk">*</span></label>
								<select
									value={quantityUnit}
									onChange={(e) => setQuantityUnit(e.target.value)}
									className="tmtSelectAngle"
								>
									<option value="MT">MT</option>
								</select>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Specification:<span className="required-asterisk">*</span></label>
								<select
									value={selectedSpecification}
									onChange={(e) => setSelectedSpecification(e.target.value)}
									className="tmtSelectAngle"
								>
									<option value="" disabled>-- Select Specification --</option>
									{specifications.map((spec, idx) => (
										<option key={idx} value={spec}>
											{spec}
										</option>
									))}
								</select>
							</div>
						</div>
						<div className="form-column-angle" style={{ paddingTop: "28px" }}>
							<div className="tmtAddItemGroupAngle">
								<button className='buttonAngle' onClick={handleAddItem}>
									Add Item
								</button>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Delivery Address:<span className="required-asterisk">*</span></label>
								<input
									type="text"
									name="address"
									placeholder="Enter Delivery Address"
									value={formData.address}
									onChange={handleFormChange}
									className="tmtInputAngle"
								/>
							</div>
						</div>
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Pincode</label>
								<input
									type="number"
									name="pincode"
									placeholder="Enter Pincode"
									min="100000"
									max="999999"
									value={formData.pincode}
									onChange={handleFormChange}
									onInput={enforceSixDigitLimit}
									className="tmtInputAngle"
								/>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Payment Terms:<span className="required-asterisk">*</span></label>
								<select
									name="payment"
									value={formData.payment}
									onChange={handleFormChange}
									className="tmtSelectAngle"
								>
									<option value="">Select payment term</option>
									{paymentTerms.map((term) => (
										<option key={term.id} value={term.name.trim()}>
											{term.name.trim()}
										</option>
									))}
								</select>
							</div>
						</div>
						{showAdvanceFields && (
							<>
								<div className="form-column">
									<div className="tmtOptionGroupAngle">
										<label className="tmtOptionLabelAngle">Advance Payment Value:<span className="required-asterisk">*</span></label>
										<input
											type="number"
											name="advanceValue"
											value={formData.advanceValue}
											onChange={handleFormChange}
											placeholder="Enter Value"

											className="tmtInputAngle"
										/>
									</div>
								</div>

								<div className="form-column">
									<div className="tmtOptionGroupAngle">
										<label className="tmtOptionLabelAngle">Advance Type:<span className="required-asterisk">*</span></label>
										<select
											name="advanceType"
											value={formData.advanceType}
											onChange={handleFormChange}
											className="tmtSelectAngle"
										>
											<option value="percentage">Percentage</option>
											<option value="fixed">Fixed Amount</option>
										</select>
									</div>
								</div>

								<div className="form-column">
									<div className="tmtOptionGroupAngle">
										<label className="tmtOptionLabelAngle">Rest paid (Specify Here):<span className="required-asterisk">*</span></label>
										<input
											type="text"
											name="restDays"
											value={formData.restDays || ''}
											onChange={handleFormChange}
											placeholder="Rest paid within days"
											min={1}
											className="tmtInputAngle"
										/>
									</div>
								</div>
							</>
						)}
						{formData.payment && formData.payment !== 'Advance' && (
							<div className="form-column">
								<div className="tmtOptionGroupAngle">
									<label className="tmtOptionLabelAngle">Enter number of days<span className="required-asterisk">*</span></label>
									<input
										type="number"
										name="paymentDays"
										value={formData.paymentDays || ''}
										onChange={handleFormChange}
										placeholder="Number of days"
										className="tmtInputAngle"
									/>
								</div>
							</div>
						)}
						<div className="tmtOptionGroupAngle">
							<label className="tmtOptionLabelAngle">Delivery Time:<span className="required-asterisk">*</span></label>
							<select
								name="delivery"
								value={formData.delivery}
								onChange={handleFormChange}
								className="tmtSelectAngle"
							>
								<option value="" disabled>Select Delivery Time</option>
								<option value="Within 7 days">Within 7 days</option>
								<option value="7-10 days">7-10 days</option>
								<option value="10-21 days">10-21 days</option>
								<option value="Others">Others</option>
							</select>
							{showDeliveryOther && (
								<input
									type="text"
									name="deliveryOther"
									placeholder="Specify Delivery Time"
									value={formData.deliveryOther}
									onChange={handleFormChange}
									className="tmtInputAngle"
								/>
							)}
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-full-angle">
							<div className="tmtOptionGroupAngle">
								<label className="tmtOptionLabelAngle">Price Basis:<span className="required-asterisk">*</span></label>
								<select
									name="priceBasis"
									value={formData.priceBasis}
									onChange={handleFormChange}
									required
									className="tmtSelectAngle"
								>
									<option value="" disabled>-- Select Price Basis --</option>
									<option value="Ex-Factory">Ex-Factory</option>
									<option value="F.O.R.">F.O.R.</option>
								</select>
							</div>
						</div>
					</div>

					<div className="form-row-angle">
						<div className="form-column-full-angle">
							<div className="tmtResetContainerAngle">
								<button className="tmtResetButtonAngle" onClick={handleReset}>
									Reset
								</button>
							</div>
						</div>
					</div>

					<div id="itemListAngle">
						<h3 className="tmtItemListTitleAngle">
							Items to Quote{' '}
							<span className="item-countAngle">
								{quoteItems.length} {quoteItems.length === 1 ? 'item' : 'items'}
							</span>
						</h3>
						<table className="items-tableAngle">
							<thead>
								<tr className="table-header-rowAngle">
									<th>SR. NO.</th>
									<th>BRAND</th>
									<th>WIDTH</th>
									<th>THICKNESS</th>
									<th>SPECIFICATION</th>
									<th>QUANTITY</th>
									<th>ACTION</th>
								</tr>
							</thead>
							<tbody>
								{quoteItems.length > 0 ? (
									quoteItems.map((item, index) => (
										<React.Fragment key={index}>
											<tr className="table-data-rowAngle">
												<td>{index + 1}</td>
												<td>{renderBrandsInTable(item.brandIds)}</td>
												<td>{item.dimensions} mm</td>
												<td>{item.thickness} mm</td>
												<td>{item.specification}</td>
												<td>{item.quantity} {item.quantityUnit}</td>
												<td>
													<button
														className="remove-btnAngle"
														onClick={(e) => removeItem(index, e)}
														title="Remove item"
													>
														<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
															<path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
														</svg>
													</button>
													<button
														className="edit-btnAngle"
														onClick={() => handleEdit(index)}
														title="Edit item"
													>
														<MdEdit />
													</button>
												</td>
											</tr>
											{editingRow === index && (
												<tr className="edit-form-rowAngle">
													<td colSpan="7">
														<div className="edit-form-containerAngle">
															<div className="edit-form-columnsAngle">
																<div className="edit-form-columnAngle">
																	<label className="edit-form-labelAngle">Brand:</label>
																	<div className="dropdown-multiselect">
																		<div
																			className="erw-dropdown-toggle"
																			onClick={(e) => {
																				e.stopPropagation();
																				setIsBrandDropdownOpen(!isBrandDropdownOpen);
																			}}
																		>
																			<span className="dropdown-arrow">
																				{selectedBrands.length > 0
																					? selectedBrands.map(b => b.name).join(", ")
																					: "Select Brand / Make"}
																			</span>
																			<FaChevronDown />
																		</div>
																		{isBrandDropdownOpen && (
																			<div className="erw-dropdown-options" onClick={(e) => e.stopPropagation()}>
																				<div className="brand-checkbox-container">
																					{brands.map((brand) => (
																						<div key={brand.id} className="brand-checkbox-item">
																							<input
																								type="checkbox"
																								id={`edit-brand-${brand.id}`}
																								name="brand"
																								value={brand.name}
																								data-id={brand.id}
																								checked={selectedBrands.some(b => b.id === brand.id)}
																								onChange={handleBrandChange}
																							/>
																							<label htmlFor={`edit-brand-${brand.id}`}>
																								{brand.name} {brand.grade && `(${brand.grade})`}
																							</label>
																						</div>
																					))}
																				</div>
																			</div>
																		)}
																	</div>

																	<label className="edit-form-labelAngle">Width (mm):</label>
																	<input
																		type="text"
																		value={item.dimensions}
																		onChange={(e) => updateItemField(index, 'dimensions', e.target.value)}
																		min="0"
																		step="0.1"
																		className="tmtInputAngle"
																	/>
																</div>

																<div className="edit-form-columnAngle">
																	<label className="edit-form-labelAngle">Thickness (mm):</label>
																	<input
																		type="text"
																		value={item.thickness}
																		onChange={(e) => updateItemField(index, 'thickness', e.target.value)}
																		min="0"
																		step="0.1"
																		className="tmtInputAngle"
																	/>
																	<div className="edit-form-columnAngle">
																		<label className="edit-form-labelAngle">Specification:</label>
																		<select
																			value={item.specification}
																			onChange={(e) => updateItemField(index, 'specification', e.target.value)}
																			className="tmtSelectAngle"
																		>
																			<option value="">-- Select --</option>
																			{specifications.map((spec, idx) => (
																				<option key={idx} value={spec}>
																					{spec}
																				</option>
																			))}
																		</select>
																	</div>
																</div>



																<div className="edit-form-columnAngle">
																	<label className="edit-form-labelAngle">Quantity:</label>
																	<div className="edit-quantity-rowAngle">
																		<input
																			type="number"
																			value={item.quantity}
																			onChange={(e) => updateItemField(index, 'quantity', e.target.value)}
																			min="0.01"
																			step="0.01"
																			className="tmtInputAngle"
																		/>
																		<select
																			value={item.quantityUnit}
																			onChange={(e) => updateItemField(index, 'quantityUnit', e.target.value)}
																			className="tmtSelectAngle"
																		>
																			<option value="KG">KG</option>
																			<option value="MT">MT</option>
																			<option value="Pc">PC</option>
																			<option value="Mtrs">MTRS</option>
																		</select>
																	</div>
																</div>
															</div>

															<div className="edit-form-buttonsAngle">
																<button
																	className="buttonAngle save-edit-btnAngle"
																	onClick={() => saveEdit(index)}
																>
																	Save Changes
																</button>
																<button
																	className="buttonAngle cancel-edit-btnAngle"
																	onClick={cancelEdit}
																>
																	Cancel
																</button>
															</div>
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									))
								) : (
									<tr className="table-empty-rowAngle">
										<td colSpan="7" className="empty-messageAngle">No items added.</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					<button
						className='buttonAngle submit-btnAngle'
						onClick={submitQuote}
						disabled={isSubmitting || quoteItems.length === 0}
					>
						{isSubmitting ? (
							<>
								<span className="spinnerAngle" role="status" aria-hidden="true"></span>
								Processing...
							</>
						) : (
							"Send for Quote"
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ColdRolledCoils;